// @ts-nocheck: converted from JS

import Loading from '@/components/Loading';
import ObservationsList from '@/components/Subjects/Show/Views/ObservationsList';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import SearchSelect from '@/components/UI/SearchSelect';
import { toWorkflowDateTime } from '@/components/Workflow/Show/Workflow.utils';
import { _isEmpty } from '@/littledash';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { WorkflowNoDataCard } from './WorkflowNoDataCard';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { errorToast, successToast } from '@/helpers.tsx';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';

const ObservationView = ({
  study,
  subject,
  observations,
  fetchAnimalDataByType,
  observed_at,
  workflow,
  onSave,
  openModal,
  canWrite,
}) => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(workflow?.preSelectedObservationTypeIds ?? []);

  const {
    response: observationsResponse,
    loading,
    error,
  } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'observations',
      perPage: -1,
      filterType: 'and',
      'filters[]': 'status|eq|active',
      order: 'asc',
    },
    options: { onError: { toast: false, slug: 'workflow-observations-load' } },
  });
  const observationTypes: Array<TeamGlossaryV1> =
    (observationsResponse?.body as { data: Array<TeamGlossaryV1> })?.data ?? [];

  const { invoke: recordNormal } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/animals/{animalApiId}/observations/normal',
    invokeOnInit: false,
    path: { studyApiId: study.api_id, animalApiId: subject.api_id },
    options: { onError: { throw: true, capture: false, toast: false, slug: 'record-normal-observation' } },
  });

  const observationTypeSections = useMemo(() => {
    const observationTypeMap = new Map(
      (observationTypes ?? []).map((observationType) => [observationType.api_id, observationType])
    );

    const selectedTypes = (selected ?? []).reduce((acc, observationTypeId) => {
      if (observationTypeMap.has(observationTypeId)) {
        acc.push(observationTypeMap.get(observationTypeId));
        observationTypeMap.delete(observationTypeId);
      }
      return acc;
    }, []);
    return [
      ...(selectedTypes.length > 0 ? [{ title: 'Selected Observation types', items: selectedTypes }] : []),
      { title: 'Observation types', items: Array.from(observationTypeMap.values()) },
    ];
  }, [selected, observationTypes]);

  const handleCallback = () => {
    onSave();
    dispatch({ type: 'CLOSE_MODAL' });
    setSelected([]);
    if (workflow.onSave !== 'next-subject') {
      fetchAnimalDataByType(subject, 'observations');
    }
  };

  const handleModalToggle = () => {
    const observationTypeMap = new Map(
      (observationTypes ?? []).map((observationType) => [observationType.api_id, observationType])
    );

    const observations = selected.reduce(
      (acc, s) => (observationTypeMap.has(s) ? [...acc, observationTypeMap.get(s)] : acc),
      []
    );

    return openModal('ADD_OBSERVATION', {
      selectedObservations: observations,
      animalId: subject.id,
      closeModal: () => dispatch({ type: 'CLOSE_MODAL' }),
      observed_at: toWorkflowDateTime(observed_at),
      handleCallback,
    });
  };

  const handleRecordNormalObservation = async () => {
    try {
      await recordNormal({
        body: {
          observed_at: toWorkflowDateTime(observed_at),
        },
        path: { studyApiId: study.api_id, animalApiId: subject.api_id },
      });
      successToast('Observation saved');
      onSave();
      setSelected([]);

      if (workflow.onSave !== 'next-subject') {
        fetchAnimalDataByType(subject, 'observations');
      }
    } catch (err) {
      const ivError = new InVivoError('Could not save observation', {
        slug: 'observation-view-record-normal',
        cause: err,
      });
      ExceptionHandler.captureException(ivError);
      errorToast('Could not save observation');
    }
  };

  const orderedObservations = observations.sort((a, b) => new Date(b.observed_at) - new Date(a.observed_at));

  return (
    <div
      className={`flex flex-wrap justify-between items-stetch h-100 ${!canWrite || (loading && 'ui__disabled')}`}
      data-test-component="ObservationView"
      data-test-element="container"
    >
      <div className="w-50 pa4 br b--moon-gray bg-light-gray" data-test-element="left-pane">
        {error && (
          <Banner critical dismiss={false} className="mw6 mv3">
            <h3 className="f5 normal lh-title pb2">An error has occurred</h3>
            <p className="f6 pb3 lh-copy">
              We are having an issue fetching the team glossary right now, please try again later. If this keeps
              occurring please contact support.
            </p>
            <Button outline critical url={'mailto:support@benchling.com'}>
              Contact support
            </Button>
          </Banner>
        )}
        {loading && <Loading />}
        {observationTypes?.length > 0 && (
          <>
            <div className="ui-card">
              <SearchSelect
                selected={selected}
                setSelected={setSelected}
                sections={observationTypeSections}
                idAccessor="api_id"
              />
            </div>
            <div className="flex flex-column" data-test-element="actions">
              <Button
                className="mt3"
                tooltip={_isEmpty(selected) ? 'Select an observation to continue' : undefined}
                disabled={_isEmpty(selected)}
                onClick={() => handleModalToggle()}
                testId="record-observation-button"
              >
                Record observation{selected.length > 1 && 's'}
              </Button>
              <Button
                className="mt3 plain"
                onClick={handleRecordNormalObservation}
                testId="record-normal-and-save-button"
              >
                Record normal and save
              </Button>
            </div>
          </>
        )}
        {observationTypes?.length === 0 && (
          <Banner info className="mw6" dismiss={false}>
            <h3 className="f5 normal basier-med lh-title">No team glossary items have been added</h3>
            <p className="f6 pt2 lh-copy">
              To get started, click on Glossary in the left hand menu and add observation glossary items.
            </p>
          </Banner>
        )}
      </div>
      <div className="w-50 bg-white" data-test-element="right-pane">
        <div className="overflow-y-scroll h-100" style={{ maxHeight: '42rem' }}>
          {orderedObservations.length ? (
            <ObservationsList
              study={study}
              subject={subject}
              openModal={openModal}
              closeModal={() => dispatch({ type: 'CLOSE_MODAL' })}
              observations={orderedObservations}
              handleCancel={() => dispatch({ type: 'CLOSE_MODAL' })}
              handleCallback={handleCallback}
            />
          ) : (
            WorkflowNoDataCard
          )}
        </div>
      </div>
    </div>
  );
};

export default ObservationView;
